import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import apiConfig from "../../../../apiConfig";

const GetAllSellerOrder = () => {
  const [orders, setOrders] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState("");
  const [loading, setLoading] = useState(true);

  const [statusCounts, setStatusCounts] = useState({
    received: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
  });

  useEffect(() => {
    fetchSellers();
  }, []);

  // ================= FETCH SELLERS =================
  const fetchSellers = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${apiConfig.baseUrl}${apiConfig.GetAllSeller}`,
      );

      setSellers(res.data.data || []);
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch sellers");
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH ORDERS =================
  const fetchOrders = async (sellerId) => {
    if (!sellerId) {
      setOrders([]);
      setStatusCounts({
        received: 0,
        processing: 0,
        shipped: 0,
        delivered: 0,
        cancelled: 0,
      });
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(
        `${apiConfig.baseUrl}${apiConfig.adminGetSellerOrders}?sellerUniqueId=${sellerId}`,
      );

      if (res.data.success) {
        setOrders(res.data.orders || []);

        setStatusCounts(
          res.data.statusCounts || {
            received: 0,
            processing: 0,
            shipped: 0,
            delivered: 0,
            cancelled: 0,
          },
        );
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to fetch seller orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // ================= HANDLERS =================
  const handleSellerChange = (value) => {
    setSelectedSeller(value);
    fetchOrders(value);
  };

  const handleRefresh = () => {
    fetchOrders(selectedSeller);
  };

  const handleClear = () => {
    setSelectedSeller("");
    setOrders([]);
    setStatusCounts({
      received: 0,
      processing: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0,
    });
  };

  // ================= STATUS COLOR =================
  const getStatusClass = (status) => {
    switch (status) {
      case "received":
        return "bg-gray-100 text-gray-700";

      case "processing":
        return "bg-yellow-100 text-yellow-700";

      case "shipped":
        return "bg-blue-100 text-blue-700";

      case "delivered":
        return "bg-green-100 text-green-700";

      case "cancelled":
        return "bg-red-100 text-red-700";

      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold">Seller Orders Management</h2>

        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col">
            <label className="text-sm font-bold mb-1">Select Seller</label>

            <select
              value={selectedSeller}
              onChange={(e) => handleSellerChange(e.target.value)}
              className="w-72 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none"
            >
              <option value="">Search Seller Orders</option>

              {sellers?.map((seller) => (
                <option key={seller._id} value={seller.sellerId}>
                  {seller.sellerId} - {seller.fullName}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleClear}
            className="px-4 py-2 border rounded-lg bg-gray-100 hover:bg-gray-200"
          >
            Remove
          </button>

          <button
            onClick={handleRefresh}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* ================= STATUS CARDS ================= */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {Object.entries(statusCounts).map(([key, value]) => (
          <div key={key} className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-800 capitalize">{key}</p>

            <h3 className="text-2xl font-bold mt-2">{value}</h3>
          </div>
        ))}
      </div>

      {/* ================= MAIN TABLE ================= */}
      <div className="bg-white rounded-xl shadow border overflow-hidden">
        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="h-12 w-12 rounded-full border-b-2 border-blue-600 animate-spin"></div>
          </div>
        ) : !selectedSeller ? (
          <div className="py-20 text-center text-gray-1000">
            Please select seller first
          </div>
        ) : orders.length === 0 ? (
          <div className="py-20 text-center">
            <h3 className="text-lg font-medium text-gray-700">
              No Orders Found
            </h3>

            <p className="text-sm text-gray-500 mt-2">
              Seller orders will appear here
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full w-max border-collapse text-sm md:text-base">
              <thead className="bg-gray-100">
                <tr>
                  {[
                    "Order ID",
                    "Customer ID",
                    "Customer Email",
                    "Items",
                    "Seller Code",
                    "Seller Email",
                    "Payment Mode",
                    "Payment Status",
                    "Shipping Cost",
                    "Total Price",
                    "Shipping Address",
                    "Created At",
                    "Status",
                  ].map((header) => (
                    <th
                      key={header}
                      className="border px-3 py-2 text-left whitespace-nowrap"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {orders.map((order) => {
                  const totalEarning =
                    order.items?.reduce(
                      (sum, item) => sum + Number(item.commissionAmount || 0),
                      0,
                    ) || 0;

                  const totalPrice =
                    order.items?.reduce(
                      (sum, item) => sum + Number(item.totalPrice || 0),
                      0,
                    ) || 0;

                  return (
                    <tr
                      key={order.orderID}
                      className="hover:bg-gray-50 align-top"
                    >
                      {/* ORDER ID */}
                      <td className="border px-3 py-2 font-semibold whitespace-nowrap">
                        {order.orderID || "-"}
                      </td>

                      {/* CUSTOMER ID */}
                      <td className="border px-3 py-2 whitespace-nowrap">
                        {order.customer?._id || "N/A"}
                      </td>

                      {/* CUSTOMER EMAIL */}
                      <td className="border px-3 py-2">
                        {order.address?.email || "-"}
                      </td>

                      {/* Items */}
                      <td className="border px-3 py-2">
                        <table className="w-full border text-xs md:text-sm">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="border px-2 py-1">Image</th>
                              <th className="border px-2 py-1">Product</th>
                              <th className="border px-2 py-1">Qty</th>
                              <th className="border px-2 py-1">Price</th>
                            </tr>
                          </thead>

                          <tbody>
                            {order?.items?.map((item, i) => (
                              <tr key={i}>
                                <td className="border px-2 py-1">
                                  {item.productImage ? (
                                    <img
                                      src={item.productImage}
                                      alt={item.title}
                                      className="w-12 h-12 object-cover rounded"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 flex items-center justify-center text-xs text-gray-400 bg-gray-100 rounded">
                                      No Image
                                    </div>
                                  )}
                                </td>

                                <td className="border px-2 py-1">
                                  {item.title || "-"}
                                </td>

                                <td className="border px-2 py-1 text-center">
                                  {item.quantity}
                                </td>

                                <td className="border px-2 py-1">
                                  ₹{item.totalPrice}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>

                      {/* SELLER CODE */}
                      <td className="border px-3 py-2 whitespace-nowrap">
                        {order.items?.[0]?.sellerCode || "-"}
                      </td>

                      {/* SELLER EMAIL */}
                      <td className="border px-3 py-2">
                        {order.items?.[0]?.sellerEmail || "-"}
                      </td>

                      {/* PAYMENT MODE */}
                      <td className="border px-3 py-2 whitespace-nowrap">
                        {order.paymentMode || "-"}
                      </td>

                      {/* PAYMENT STATUS */}
                      <td className="border px-3 py-2 whitespace-nowrap">
                        {order.paymentStatus || "-"}
                      </td>

                      {/* SHIPPING COST */}
                      <td className="border px-3 py-2 font-medium whitespace-nowrap">
                        ₹{order.shippingCost || 0}
                      </td>

                      {/* TOTAL PRICE */}
                      <td className="border px-3 py-2 font-semibold text-blue-600 whitespace-nowrap">
                        ₹{totalPrice}
                      </td>

                      {/* SHIPPING ADDRESS */}
                      <td className="border px-3 py-2 min-w-[220px]">
                        <div className="font-medium">
                          {order.address?.name || "N/A"}
                        </div>

                        <div>{order.address?.location || ""}</div>

                        <div>
                          {order.address?.city || ""},{" "}
                          {order.address?.state || ""}
                        </div>

                        <div>{order.address?.pincode || ""}</div>

                        <div>{order.address?.phone || ""}</div>
                      </td>

                      {/* CREATED AT */}
                      <td className="border px-3 py-2 whitespace-nowrap">
                        {new Date(order.createdAt).toLocaleDateString()}
                        <br />
                        <span className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </span>
                      </td>

                      {/* STATUS */}
                      <td className="border px-3 py-2 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 rounded-lg text-sm font-medium ${getStatusClass(
                            order.status,
                          )}`}
                        >
                          {order.status || "-"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GetAllSellerOrder;
